/* eslint-disable i18next/no-literal-string */

// Single source of truth for the missing-profile-data modal wording.
// Merged into the i18n resources of all three frontends (my-bubbles-react,
// bb-kiosk-fe-react, bb-mobile) AFTER their local catalogs, so the values
// here win at runtime. Edit wording here, not in the per-app catalogs.
//
// The modal's title and body are NOT here: they are admin-configurable and
// arrive server-resolved on the status endpoint — defaults live in
// common/src/logic/missing-profile-data-texts.ts.
//
// Only copy specific to this flow lives here — generic labels the apps share
// with other screens ("Save", "Country", "Zip code", "Got it", ...) stay in
// the per-app catalogs.

export type MissingProfileDataLanguage = 'en' | 'hu' | 'de' | 'sk';

type TranslatedText = Record<MissingProfileDataLanguage, string>;

export const missingProfileDataTranslations = {

  // Supplementary-field widgets. The field/option labels live here because
  // kiosk and mobile have no other screen using these keys; the values mirror
  // the my-bubbles catalog (which this file overrides with identical strings).
  'Please select': {
    en: 'Please select',
    hu: 'Kérjük, válassz',
    de: 'Bitte auswählen',
    sk: 'Prosím, vyber',
  },
  'Phone number': {
    en: 'Phone number',
    hu: 'Telefonszám',
    de: 'Telefonnummer',
    sk: 'Telefónne číslo',
  },
  'Education': {
    en: 'Education',
    hu: 'Végzettség',
    de: 'Bildung',
    sk: 'Vzdelanie',
  },
  'Gender': {
    en: 'Gender',
    hu: 'Nem',
    de: 'Geschlecht',
    sk: 'Pohlavie',
  },
  'Marital status': {
    en: 'Marital status',
    hu: 'Családi állapot',
    de: 'Familienstand',
    sk: 'Rodinný stav',
  },
  'Male': {
    en: 'Male',
    hu: 'Férfi',
    de: 'Männlich',
    sk: 'Muž',
  },
  'Female': {
    en: 'Female',
    hu: 'Nő',
    de: 'Weiblich',
    sk: 'Žena',
  },
  'Single': {
    en: 'Single',
    hu: 'Egyedülálló',
    de: 'Ledig',
    sk: 'Slobodný/á',
  },
  'Married': {
    en: 'Married',
    hu: 'Házas',
    de: 'Verheiratet',
    sk: 'Ženatý/Vydatá',
  },
  'Divorced': {
    en: 'Divorced',
    hu: 'Elvált',
    de: 'Geschieden',
    sk: 'Rozvedený/á',
  },
  'Widow': {
    en: 'Widow',
    hu: 'Özvegy',
    de: 'Witwe',
    sk: 'Vdova/Vdovec',
  },
  'None': {
    en: 'None',
    hu: 'Nincs',
    de: 'Keine',
    sk: 'Žiadne',
  },
  'Primary school': {
    en: 'Primary school',
    hu: 'Általános iskola',
    de: 'Grundschule',
    sk: 'Základná škola',
  },
  'Skilled worker': {
    en: 'Skilled worker',
    hu: 'Szakmunkás',
    de: 'Facharbeiter',
    sk: 'Vyučený/á',
  },
  'Secondary school': {
    en: 'Secondary school',
    hu: 'Középiskola',
    de: 'Weiterführende Schule',
    sk: 'Stredná škola',
  },
  'Bachelor degree': {
    en: 'Bachelor degree',
    hu: 'Főiskola',
    de: 'Bachelor-Abschluss',
    sk: 'Bakalársky titul',
  },
  'Masters degree': {
    en: 'Masters degree',
    hu: 'Egyetem',
    de: 'Master-Abschluss',
    sk: 'Magisterský titul',
  },

  // Residence-type selector (my-bubbles uses the address toggle,
  // kiosk/mobile use the yes/no question)
  'I have a foreign address': {
    en: 'I have a foreign address',
    hu: 'Külföldi címmel rendelkezem',
    de: 'Ich habe eine ausländische Adresse',
    sk: 'Mám zahraničnú adresu',
  },
  'I have a domestic address': {
    en: 'I have a domestic address',
    hu: 'Belföldi címmel rendelkezem',
    de: 'Ich habe eine inländische Adresse',
    sk: 'Mám domácu adresu',
  },
  'Do you live in {{countryName}}?': {
    en: 'Do you live in {{countryName}}?',
    hu: '{{countryName}} területén laksz?',
    de: 'Wohnst du in {{countryName}}?',
    sk: 'Bývate v krajine {{countryName}}?',
  },

  // Actions
  'Get the coupon': {
    en: 'Get the coupon',
    hu: 'Kupont kérek',
    de: 'Gutschein anfordern',
    sk: 'Žiadam kupón',
  },
  "I'll provide it": {
    en: "I'll provide it",
    hu: 'Megadom',
    de: 'Angeben',
    sk: 'Zadám',
  },
  'Skip for now': {
    en: 'Skip for now',
    hu: 'Kihagyom',
    de: 'Überspringen',
    sk: 'Preskočiť',
  },

  // Success views
  "Here's your coupon code!": {
    en: "Here's your coupon code!",
    hu: 'Itt a kuponkódod!',
    de: 'Hier ist dein Gutscheincode!',
    sk: 'Tu je tvoj kupónový kód!',
  },
  'Use it on your next wash.': {
    en: 'Use it on your next wash.',
    hu: 'Használd a következő mosáshoz.',
    de: 'Verwende ihn für deine nächste Wäsche.',
    sk: 'Použi ho na ďalšie pranie.',
  },
  'Thank you!': {
    en: 'Thank you!',
    hu: 'Köszönjük!',
    de: 'Danke!',
    sk: 'Ďakujeme!',
  },
  'Your data has been saved.': {
    en: 'Your data has been saved.',
    hu: 'Az adataidat elmentettük.',
    de: 'Deine Daten wurden gespeichert.',
    sk: 'Tvoje údaje boli uložené.',
  },

  // Errors & validation (also used by the personal-details forms)
  'Could not save your profile data. Please try again.': {
    en: 'Could not save your profile data. Please try again.',
    hu: 'Nem sikerült menteni a profiladataidat. Kérjük, próbáld újra.',
    de: 'Profildaten konnten nicht gespeichert werden. Bitte versuche es erneut.',
    sk: 'Nepodarilo sa uložiť údaje profilu. Skús to znova.',
  },
  'Zip code must be {{digits}} digits': {
    en: 'Zip code must be {{digits}} digits',
    hu: 'Az irányítószám {{digits}} számjegyű kell legyen',
    de: 'Die Postleitzahl muss {{digits}} Ziffern haben',
    sk: 'PSČ musí mať {{digits}} číslic',
  },
  'Enter a valid country': {
    en: 'Enter a valid country',
    hu: 'Adj meg egy érvényes országot',
    de: 'Gib ein gültiges Land ein',
    sk: 'Zadajte platnú krajinu',
  },
  'Enter a valid phone number (e.g. {{example}})': {
    en: 'Enter a valid phone number (e.g. {{example}})',
    hu: 'Adj meg egy érvényes telefonszámot (pl. {{example}})',
    de: 'Gib eine gültige Telefonnummer ein (z. B. {{example}})',
    sk: 'Zadaj platné telefónne číslo (napr. {{example}})',
  },
} satisfies Record<string, TranslatedText>;

export function missingProfileDataResources(lang: MissingProfileDataLanguage): Record<string, string> {
  return Object.fromEntries(
    Object.entries(missingProfileDataTranslations).map(([key, value]) => [key, value[lang]]),
  );
}
